import { Link } from "react-router-dom";
import { Clock, Eye, User } from "lucide-react";
import { getCategory } from "../lib/categories";
import { timeAgo } from "../lib/api";

const CategoryBadge = ({ slug, sub, tone = "dark" }) => {
  const cat = getCategory(slug);
  const textColor = tone === "light" ? "#FFFFFF" : "#0A0A0A";
  return (
    <span className="meta-label font-bold inline-flex items-center gap-1.5" style={{ color: textColor }}>
      <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
      {cat.name}{sub ? ` · ${sub}` : ""}
    </span>
  );
};

export { CategoryBadge };

const FALLBACK = "https://images.unsplash.com/photo-1504711434969-e33886168f5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function ArticleCard({ article, variant = "standard" }) {
  const to = `/${article.category}/${article.slug}`;
  const img = article.cover_image || FALLBACK;

  if (variant === "hero") {
    return (
      <Link to={to} data-testid={`article-card-hero-${article.slug}`} className="group block relative overflow-hidden rounded-lg h-full min-h-[420px]">
        <img src={img} alt={article.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
        <div className="absolute bottom-0 p-6 sm:p-8 text-white">
          <CategoryBadge slug={article.category} sub={article.subcategory} tone="light" />
          <h2 className="font-headline text-3xl sm:text-4xl font-bold leading-tight mt-3 mb-3 group-hover:text-[#FDA4AF] transition-colors">{article.title}</h2>
          <p className="font-body text-slate-200 text-base line-clamp-2 max-w-2xl text-justify">{article.summary}</p>
          <div className="flex items-center gap-4 mt-4 meta-label text-slate-300">
            <span className="flex items-center gap-1"><User size={12} />{article.author}</span>
            <span className="flex items-center gap-1"><Clock size={12} />{timeAgo(article.published_at)}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "side") {
    return (
      <Link to={to} data-testid={`article-card-side-${article.slug}`} className="group flex gap-3 pb-4 border-b border-[#E2E8F0] last:border-0">
        <img src={img} alt={article.title} className="w-24 h-20 object-cover rounded shrink-0" />
        <div>
          <CategoryBadge slug={article.category} />
          <h3 className="font-headline text-base font-bold leading-snug mt-1 text-[#0A0A0A] group-hover:text-[#E11D48] transition-colors line-clamp-3">{article.title}</h3>
        </div>
      </Link>
    );
  }

  if (variant === "list") {
    return (
      <Link to={to} data-testid={`article-card-list-${article.slug}`} className="group flex gap-4 py-4 border-b border-[#E2E8F0]">
        <div>
          <CategoryBadge slug={article.category} sub={article.subcategory} />
          <h3 className="font-headline text-xl font-bold leading-snug mt-1.5 mb-1 text-[#0A0A0A] group-hover:text-[#E11D48] transition-colors">{article.title}</h3>
          <p className="font-body text-sm text-slate-600 line-clamp-2 text-justify">{article.summary}</p>
          <div className="flex items-center gap-4 mt-2 meta-label text-slate-500">
            <span>{article.author}</span><span>{timeAgo(article.published_at)}</span>
          </div>
        </div>
        <img src={img} alt={article.title} className="w-40 h-28 object-cover rounded shrink-0 order-first sm:order-last" />
      </Link>
    );
  }

  // standard card
  return (
    <Link to={to} data-testid={`article-card-${article.slug}`} className="group flex flex-col bg-white rounded-lg overflow-hidden border border-[#E2E8F0] hover:shadow-lg transition-shadow">
      <div className="overflow-hidden aspect-[16/10]">
        <img src={img} alt={article.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <CategoryBadge slug={article.category} sub={article.subcategory} />
        <h3 className="font-headline text-xl font-bold leading-snug mt-2 mb-2 text-[#0A0A0A] group-hover:text-[#E11D48] transition-colors line-clamp-2">{article.title}</h3>
        <p className="font-body text-sm text-slate-600 line-clamp-3 flex-1 text-justify">{article.summary}</p>
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[#F1F5F9] meta-label text-slate-500">
          <span className="flex items-center gap-1"><User size={12} />{article.author}</span>
          <span className="flex items-center gap-1"><Clock size={12} />{timeAgo(article.published_at)}</span>
          <span className="flex items-center gap-1 ml-auto"><Eye size={12} />{article.views}</span>
        </div>
      </div>
    </Link>
  );
}
